import React, { useState } from 'react';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';

import CtaLink from 'components/essentials/Cta/CtaLink';
import notificationBannerHelper from 'components/consumer/NotificationBanner/utils';
import ProductImage from 'components/consumer/cart/components/ProductImage';
import useProtectionPlans from 'data-hooks/useProtectionPlans';
import IconProtectionPlan from 'components/icons/iconProtectionPlan';
import IconPetProtector from 'components/icons/iconPetProtector';
import LoadMoreButton from 'components/consumer/LoadMoreButton';
import ProtectionPlans from 'components/consumer/ProtectionPlans';
import useUserData from 'data-hooks/useUserData';
import UserProtectionPlanModal from '../UserProtectionPlanModal';

export const SelectedProtectionPlan = ({ applied_protection_plan }) => (
  <div className="flex flex-row items-center bg-white mr-auto p-[5px] h-[60px] border border-solid border-gray-light7 max-[1023px]:w-full max-[1023px]:p-[5px] max-[1023px]:mt-2">
    <span className="mr-[.7rem]">
      {applied_protection_plan.plan_name.includes('Pet Protector Plan') ? (
        <IconPetProtector />
      ) : (
        <IconProtectionPlan />
      )}
    </span>
    <div className="flex w-full justify-between items-center max-[1023px]:flex-col max-[1023px]:items-start">
      <span className="text-xs font-bold mr-4">
        5-year {applied_protection_plan.plan_name}
        {` (+$`}
        {Intl.NumberFormat().format(applied_protection_plan.plan_price)})
      </span>
      <span>
        <CtaLink
          target="_blank"
          to="/furniture-protection-plan/"
          styling="link"
          noLinkPadding
          color="brand"
          hasArrow={false}
          textClassName="!text-xs !font-bold"
        >
          Details
        </CtaLink>
      </span>
    </div>
  </div>
);

SelectedProtectionPlan.propTypes = {
  applied_protection_plan: PropTypes.shape({
    plan_name: PropTypes.string,
    plan_price: PropTypes.bool,
  }),
};

const UserProtectionPlans = () => {
  const {
    data: {
      error,
      loading,
      loadingMore,
      cartItems,
      hasLoadMore,
      isUpdateSuccess,
      purchaseErrorMessage,
    },
    actions: {
      logout,
      loadMore,
      purchaseProtectionPlan,
      setIsUpdateSuccess,
      setPurchaseErrorMessage,
    },
  } = useProtectionPlans();

  const { isImpersonatingUser } = useUserData();

  const [state, setState] = useState({
    isOpen: false,
    orderId: null,
    cartItem: null,
    selectedPlan: null,
  });

  if (loading) return <div>loading...</div>;

  if (error) {
    const code = get(error, '0.code');
    const message = get(error, '0.message');
    if (code === 'AUTH_REQUIRED' || (message && message.includes('JWT'))) {
      logout();
      return <Redirect to="/sign-in/" />;
    }
    return `Error!: ${get(error, '0.message', 'Failed to fetch the orders')}`;
  }

  if (isUpdateSuccess && state.selectedPlan) {
    notificationBannerHelper.showMsg('UserOrdersProtectionPlan', {
      planName: state.selectedPlan?.plan_name,
      productName: state.cartItem?.name,
    });
    setState({
      ...state,
      isOpen: false,
      selectedPlan: null,
      orderId: null,
      cartItem: null,
    });
    setIsUpdateSuccess(false);
    setPurchaseErrorMessage(null);
  }

  const validCartItems = cartItems?.filter(
    orderItem =>
      !['Canceled', 'Delivered', 'Completed', 'Replacement'].includes(
        orderItem.order_status
      )
  );
  const hasOrdersEligibleForProtectionPlans = validCartItems?.some(
    item =>
      item.applied_protection_plan || item.available_protection_plans?.length
  );

  return (
    <>
      <div>
        {hasOrdersEligibleForProtectionPlans ? (
          <>
            {validCartItems?.map(item => (
              <div
                key={item.cart_id}
                className="bg-gray-light8 mb-12 border border-solid border-gray-light7"
              >
                <div className="flex flex-row items-center p-[.7rem] h-[6.9rem] max-[1023px]:flex-col max-[1023px]:h-[unset]">
                  <div className="flex flex-row mr-auto items-center w-1/2 max-[1023px]:w-full">
                    <ProductImage
                      product={item}
                      width={100}
                      trim="color"
                      trimPad={20}
                      containerClass="mr-8"
                    />
                    <div className="text-base font-bold">
                      <div>{item.name}</div>
                      <div className="text-xs font-bold">
                        {`Qty: ${item.quantity}`}
                      </div>
                    </div>
                  </div>
                  {item.applied_protection_plan ? (
                    <SelectedProtectionPlan
                      applied_protection_plan={item.applied_protection_plan}
                    />
                  ) : null}
                </div>

                {!item.applied_protection_plan && (
                  <ProtectionPlans
                    eligiblePlans={[...item.available_protection_plans].sort(
                      (a, b) => a.plan_id - b.plan_id
                    )}
                    forPage="UserDashboardPP"
                    onAdd={planId => {
                      if (!isImpersonatingUser && planId) {
                        setState({
                          isOpen: true,
                          cartItem: item,
                          selectedPlan: item.available_protection_plans.find(
                            plan => plan.plan_id === planId
                          ),
                          orderId: item.order_id,
                        });
                      }
                    }}
                    selectedPlan={item.applied_protection_plan}
                    showInactiveProtectionPlan
                  />
                )}
              </div>
            ))}
            {!!hasLoadMore && (
              <LoadMoreButton
                label="Load more"
                onClick={loadMore}
                loading={loadingMore}
              />
            )}
          </>
        ) : (
          <div className="delayed-display">
            You currently don’t have any orders that are eligible for a
            Protection Plan.
          </div>
        )}
      </div>
      {state.isOpen && (
        <UserProtectionPlanModal
          onClose={() => {
            setPurchaseErrorMessage(null);
            setState({
              ...state,
              isOpen: false,
              selectedPlan: null,
              orderId: null,
              cartItem: null,
            });
          }}
          orderId={state.orderId}
          cartItem={state.cartItem}
          selectedPlan={state.selectedPlan}
          updateOrder={purchaseProtectionPlan}
          purchaseErrorMessage={purchaseErrorMessage}
        />
      )}
    </>
  );
};

export default UserProtectionPlans;
